import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import MailTemplate from "./mailTemplate";
import OtherMailTemplates from "./OtherEmail";
import PurchaseFailedTemplate from "./PurchaseFailedTemplate";
import PurchaseSuccessTemplate from "./PurchaseSuccessTemplate";
import ManualGrantNewUserTemplate from "./ManualGrantNewUserTemplate";
import ManualGrantAddTourTemplate from "./ManualGrantAddTourTemplate";

/**
 * Mail Templates — Option C layout
 * ---------------------------------------------------------------------------
 * Two-column page: a left-hand sidebar groups templates by how they fire
 * ("Scheduled" — fire on cron/timer; "Event-triggered" — fire on an action
 * like a purchase or manual grant). This scales better than horizontal tabs
 * as more templates are added and gives admins a clearer mental model
 * (periodic vs. reactive emails).
 * ---------------------------------------------------------------------------
 */

type TabId =
  | "redeem"
  | "global"
  | "otherMail"
  | "upsell"
  | "purchaseSuccess"
  | "purchaseFailed"
  | "manualGrantNewUser"
  | "manualGrantAddTour";

type TabGroup = {
  label: string;
  description: string;
  tabs: Array<{
    id: TabId;
    label: string;
    description: string;
    icon: JSX.Element;
  }>;
};

const icons = {
  redeem: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>
  ),
  globe: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  ),
  mail: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
  ),
  upsell: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
  ),
  check: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  ),
  warning: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  ),
  userPlus: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
  ),
  plusDoc: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12-5v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
  ),
};

const groups: TabGroup[] = [
  {
    label: "Scheduled",
    description: "Fire on a timer or cron",
    tabs: [
      { id: "redeem",   label: "Redeem",       description: "Sent after a booking, once elapsed time passes", icon: icons.redeem },
      { id: "global",   label: "Bokun Global", description: "Bokun-wide transactional mail template",         icon: icons.globe },
      { id: "otherMail",label: "Other Mail",   description: "Ad-hoc / announcement emails",                   icon: icons.mail },
      { id: "upsell",   label: "Upsell",       description: "2h after redemption, recommends more tours",     icon: icons.upsell },
    ],
  },
  {
    label: "Event-triggered",
    description: "Fire on a specific action",
    tabs: [
      { id: "purchaseSuccess",    label: "Purchase Success",        description: "After a successful iOS/Android IAP purchase",    icon: icons.check },
      { id: "purchaseFailed",     label: "Purchase Failed",         description: "When an iOS IAP validation rejection occurs",    icon: icons.warning },
      { id: "manualGrantNewUser", label: "Manual Grant — New User", description: "Admin creates user + grants first tour",         icon: icons.userPlus },
      { id: "manualGrantAddTour", label: "Manual Grant — Tour Added", description: "Admin adds another tour to an existing user",  icon: icons.plusDoc },
    ],
  },
];

const MailPage = () => {
  const [activeTab, setActiveTab] = React.useState<TabId>("redeem");
  useEffect(() => {}, []);
  const { adminPermission } = useSelector((state: any) => state.counter);

  if (adminPermission?.is_admin == 2 && !adminPermission?.mailTemplate) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <div className="bg-white rounded-2xl shadow-lg px-10 py-8 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
          <h1 className="text-2xl font-semibold text-gray-700 font-openSans">You don't have permission</h1>
          <p className="text-gray-400 mt-2 text-sm">Contact your administrator for access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-10 py-8">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#1a1a2e] font-openSans">Mail Templates</h1>
        <p className="text-sm text-[#6B7280] mt-1">Manage email templates grouped by how they fire</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <aside className="w-full lg:w-72 flex-shrink-0">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden sticky top-4">
            {groups.map((group, groupIdx) => (
              <div key={group.label} className={groupIdx > 0 ? "border-t border-gray-100" : ""}>
                <div className="px-4 pt-4 pb-2">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#0e7c77]">{group.label}</p>
                  <p className="text-[11px] text-[#9ca3af] mt-0.5">{group.description}</p>
                </div>
                <nav className="pb-3">
                  {group.tabs.map((tab) => {
                    const active = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-start gap-3 px-4 py-2.5 text-left transition-colors duration-150 ${
                          active
                            ? "bg-[#E8F8F5] border-l-4 border-[#6ECDC5] text-[#0e7c77]"
                            : "border-l-4 border-transparent text-[#374151] hover:bg-gray-50"
                        }`}
                      >
                        <div className={`mt-0.5 flex-shrink-0 ${active ? "text-[#6ECDC5]" : "text-[#9ca3af]"}`}>
                          {tab.icon}
                        </div>
                        <div className="min-w-0">
                          <div className={`text-sm font-semibold leading-tight ${active ? "text-[#0e7c77]" : "text-[#1a1a2e]"}`}>
                            {tab.label}
                          </div>
                          <div className="text-[11px] text-[#6B7280] mt-0.5 leading-snug">
                            {tab.description}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </nav>
              </div>
            ))}
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className={activeTab === "redeem" ? "block" : "hidden"}>
              <MailTemplate mailType="redeem" />
            </div>
            <div className={activeTab === "global" ? "block" : "hidden"}>
              <MailTemplate mailType="global" />
            </div>
            <div className={activeTab === "otherMail" ? "block" : "hidden"}>
              <OtherMailTemplates />
            </div>
            <div className={activeTab === "upsell" ? "block" : "hidden"}>
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <strong>Upsell emails</strong> are sent automatically 2 hours after a user redeems a tour code.
                They recommend other tours and bundles in the same city.
                <br />
                <strong>Available variables:</strong> <code>{"${name}"}</code>, <code>{"${productName}"}</code>, <code>{"${cityName}"}</code>, <code>{"${tourCount}"}</code>, <code>{"${tourList}"}</code>, <code>{"${bundleList}"}</code>, <code>{"${bundleName}"}</code>, <code>{"${bundlePrice}"}</code>
              </div>
              <MailTemplate mailType="upsell" />
            </div>
            <div className={activeTab === "purchaseSuccess" ? "block" : "hidden"}>
              <PurchaseSuccessTemplate />
            </div>
            <div className={activeTab === "purchaseFailed" ? "block" : "hidden"}>
              <PurchaseFailedTemplate />
            </div>
            <div className={activeTab === "manualGrantNewUser" ? "block" : "hidden"}>
              <ManualGrantNewUserTemplate />
            </div>
            <div className={activeTab === "manualGrantAddTour" ? "block" : "hidden"}>
              <ManualGrantAddTourTemplate />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MailPage;
